/**
 * 인증 API 클라이언트
 */

import { safeFetch } from "./http-client";
import { APIError, isAuthServiceUnavailableError } from "./errors";

export interface RegisterRequest {
  email: string;
  password: string;
}

export interface RegisterResponse {
  success: boolean;
  user_id?: string;
  error?: string;
  error_code?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  user_id?: string;
  error?: string;
  error_code?: string;
}

export interface VerifyEmailResponse {
  success: boolean;
  message?: string;
  error?: string;
  error_code?: string;
}

export interface ResendVerificationResponse {
  success: boolean;
  message?: string;
  error?: string;
  error_code?: string;
}

export interface LoginOptions {
  updateLocalAuthHint?: boolean;
}

export type OAuthProvider = "google" | "apple";

export interface LogoutOptions {
  updateLocalAuthHint?: boolean;
}

export interface UserInfo {
  id: string;
  email: string;
  /**
   * 관리자 전용 진입점(API 문서 등)을 노출할지 결정하는 서버 힌트.
   * 실제 접근 차단은 서버가 하므로, 값이 없으면 일반 사용자로 취급한다.
   */
  is_admin?: boolean;
}

export interface MeResponse {
  success: boolean;
  user?: UserInfo;
  error?: string;
}

export interface MeOptions {
  updateLocalAuthHint?: boolean;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  error?: string;
  error_code?: string;
}

export interface UserSettings {
  startingPot?: number;
  effectiveStack?: number;
  rakePercent?: number;
  rakeCap?: number;
  [key: string]: unknown;
}

export interface GetSettingsResponse {
  success: boolean;
  settings?: UserSettings;
  error?: string;
}

export interface GetSettingsOptions {
  updateLocalAuthHint?: boolean;
}

export interface UpdateSettingsRequest {
  settings: UserSettings;
}

export interface UpdateSettingsResponse {
  success: boolean;
  error?: string;
}

// 인증 토큰은 backend 가 발급하는 httpOnly 쿠키(auth_token)로만 운반된다. JS 에서 토큰을
// 읽거나 저장하지 않아 XSS 토큰 절취를 차단한다 (LOC-139). localStorage 에는 토큰이 아닌
// 비민감 사용자 정보(id/email)만 "로그인 힌트"로 저장하며, 실제 인증은 쿠키가 담당한다.
const USER_KEY = "auth_user";

class AuthClient {
  private baseUrl: string;

  constructor(baseUrl = "") {
    this.baseUrl = baseUrl;
  }

  /**
   * 신규 사용자 등록
   */
  async register(request: RegisterRequest): Promise<RegisterResponse> {
    return safeFetch<RegisterResponse>(`${this.baseUrl}/api/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(request),
    });
  }

  /**
   * 이메일/비밀번호로 로그인
   */
  async login(
    request: LoginRequest,
    options: LoginOptions = {}
  ): Promise<LoginResponse> {
    const updateLocalAuthHint = options.updateLocalAuthHint ?? true;

    const data = await safeFetch<LoginResponse>(
      `${this.baseUrl}/api/auth/login`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(request),
      }
    );

    // 토큰은 httpOnly 쿠키(Set-Cookie)로 받으므로 JS 에 저장하지 않는다.
    // 로그인 힌트용 비민감 사용자 정보만 저장한다.
    if (data.success && updateLocalAuthHint) {
      this.saveUser({ id: data.user_id ?? "", email: request.email });
    }

    return data;
  }

  async verifyEmail(token: string): Promise<VerifyEmailResponse> {
    return safeFetch<VerifyEmailResponse>(
      `${this.baseUrl}/api/auth/verify-email`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ token }),
      }
    );
  }

  async resendVerification(email: string): Promise<ResendVerificationResponse> {
    return safeFetch<ResendVerificationResponse>(
      `${this.baseUrl}/api/auth/resend-verification`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ email }),
      }
    );
  }

  startOAuth(
    provider: OAuthProvider,
    redirectTo = window.location.pathname + window.location.search
  ): void {
    const target = `${
      this.baseUrl
    }/api/auth/oauth/${provider}/start?redirect_to=${encodeURIComponent(
      redirectTo
    )}`;
    window.location.assign(target);
  }

  /**
   * 로그아웃
   */
  async logout(options: LogoutOptions = {}): Promise<void> {
    const updateLocalAuthHint = options.updateLocalAuthHint ?? true;

    // 세션 쿠키가 credentials:"include" 로 자동 전송된다. backend 가 토큰을 blacklist 하고
    // 쿠키를 만료시킨다 (idempotent — 쿠키가 없어도 성공).
    try {
      await safeFetch(`${this.baseUrl}/api/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
    } catch (error) {
      if (isAuthServiceUnavailableError(error)) {
        throw error;
      }
      // 로그아웃 시 에러 무시 — 로컬 힌트는 아래에서 정리한다.
    }
    if (updateLocalAuthHint) {
      this.clearAuth();
    }
  }

  /**
   * 현재 사용자 정보 조회
   */
  async me(options: MeOptions = {}): Promise<MeResponse> {
    const updateLocalAuthHint = options.updateLocalAuthHint ?? true;

    // 인증은 httpOnly 쿠키가 담당한다 (credentials:"include"). 쿠키가 없으면 backend 가
    // 401 을 반환하고 아래 catch 에서 로컬 힌트를 정리한다.
    try {
      const data = await safeFetch<MeResponse>(`${this.baseUrl}/api/auth/me`, {
        method: "GET",
        credentials: "include",
      });

      if (data.success && data.user && updateLocalAuthHint) {
        this.saveUser(data.user);
      } else if (updateLocalAuthHint) {
        this.clearAuth();
      }

      return data;
    } catch (error) {
      if (
        error instanceof APIError &&
        error.statusCode === 401 &&
        updateLocalAuthHint
      ) {
        this.clearAuth();
      }
      throw error;
    }
  }

  /**
   * 인증 여부 확인 (낙관적 힌트).
   * 실제 인증은 httpOnly 쿠키가 담당하므로 JS 로 토큰 존재를 확인할 수 없다. 저장된
   * 비민감 사용자 정보 유무를 로그인 힌트로 사용하고, 정확한 검증은 me() 의 401 처리가 한다.
   */
  isAuthenticated(): boolean {
    return !!this.getUser();
  }

  /**
   * 저장된 사용자 정보 반환
   */
  getUser(): UserInfo | null {
    const userStr = localStorage.getItem(USER_KEY);
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * 사용자 정보를 localStorage에 저장 (비민감 — 토큰 아님)
   */
  private saveUser(user: UserInfo): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  saveUserHint(user: UserInfo): void {
    this.saveUser(user);
  }

  /**
   * 로컬 인증 힌트 삭제. 세션 쿠키 자체는 backend logout 이 만료시킨다.
   */
  clearAuth(): void {
    localStorage.removeItem(USER_KEY);
  }

  /**
   * 비밀번호 변경
   */
  async changePassword(
    request: ChangePasswordRequest
  ): Promise<ChangePasswordResponse> {
    try {
      return await safeFetch<ChangePasswordResponse>(
        `${this.baseUrl}/api/auth/password`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(request),
        }
      );
    } catch (error) {
      if (error instanceof APIError && error.statusCode === 401) {
        this.clearAuth();
      }
      throw error;
    }
  }

  /**
   * 사용자 설정 조회
   */
  async getSettings(
    options: GetSettingsOptions = {}
  ): Promise<GetSettingsResponse> {
    const updateLocalAuthHint = options.updateLocalAuthHint ?? true;

    try {
      return await safeFetch<GetSettingsResponse>(
        `${this.baseUrl}/api/user/settings`,
        {
          method: "GET",
          credentials: "include",
        }
      );
    } catch (error) {
      if (
        error instanceof APIError &&
        error.statusCode === 401 &&
        updateLocalAuthHint
      ) {
        this.clearAuth();
      }
      throw error;
    }
  }

  /**
   * 사용자 설정 업데이트
   */
  async updateSettings(
    request: UpdateSettingsRequest
  ): Promise<UpdateSettingsResponse> {
    try {
      return await safeFetch<UpdateSettingsResponse>(
        `${this.baseUrl}/api/user/settings`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(request),
        }
      );
    } catch (error) {
      if (error instanceof APIError && error.statusCode === 401) {
        this.clearAuth();
      }
      throw error;
    }
  }
}

// 싱글턴 인스턴스
export const authClient = new AuthClient();
